import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useSelectedFeaturedPeople } from '@/features/featured-people/api/get-featured-people';
import { TFeaturedPeopleType } from '@/features/featured-people/types';
import { useSelectedLogos } from '@/features/logos/api/get-logos';
import { logosQueryKeys } from '@/features/logos/api/query-keys';
import { TLogo } from '@/features/logos/types';
import { findCachedEntry } from '@/hooks/use-catalogue-picker';
import { normalizePresentIds } from '@/utils/ids';

type Options = {
  /** For views that list rows, so names do not blank while a changed set loads. */
  keepPrevious?: boolean;
};

const NO_IDS: (string | null | undefined)[] = [];

function useIdSet(ids: readonly (string | null | undefined)[]) {
  const key = normalizePresentIds(ids).join(',');
  return useMemo(() => (key ? key.split(',') : []), [key]);
}

/** The logos of the ids a view shows, by id, resolved with one request per id set. */
export function useLogosById(
  ids: readonly (string | null | undefined)[] = NO_IDS,
  { keepPrevious = false }: Options = {}
) {
  const idSet = useIdSet(ids);
  const query = useSelectedLogos(idSet, { keepPrevious });
  const byId = useMemo(
    () => new Map<string, TLogo>((query.data?.data ?? []).map(logo => [logo.id, logo])),
    [query.data]
  );
  return { byId, query };
}

/**
 * Looks a logo up among the answers already cached: the ids a view resolved and the pages a
 * picker listed. Enough for an id the reviewer has just chosen, which was on screen to choose.
 */
export function useCachedLogoLookup() {
  const queryClient = useQueryClient();
  return useCallback(
    (id: string | null | undefined) =>
      id ? findCachedEntry<TLogo>(queryClient, logosQueryKeys.all, id) : undefined,
    [queryClient]
  );
}

/** The people of the ids a view shows, by id, resolved with one request per id set. */
export function usePeopleById(
  ids: readonly (string | null | undefined)[] = NO_IDS,
  { keepPrevious = false }: Options = {}
) {
  const idSet = useIdSet(ids);
  const query = useSelectedFeaturedPeople(idSet, { keepPrevious });
  const byId = useMemo(
    () =>
      new Map<string, TFeaturedPeopleType>(
        (query.data?.data ?? []).map(person => [person.id, person])
      ),
    [query.data]
  );
  return { byId, query };
}
